import { Helpers } from '@smiles/core-airlines';
import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  lastDestinationsLocalStorageKey,
  lastOriginsLocalStorageKey,
  lastSearchLocalStorageKey,
  lastSearchLocalStorageKeyLegacy,
} from '../common/constants';
import {
  ICustomAutoCompleteSearch,
  IFlightClass,
  IFlightPassagers,
  IFlightType,
  IGenerateSearchURL,
  IInfoModal,
  ISearchDate,
  ISearchErrors,
  ISearchFlight,
  ISubmitSearch,
  cabinTypesList,
  flightClassesList,
  flightTypesList,
  getModalType,
} from '../common/types';


import {
  generateSearchUrl,
  searchAirport,
  formatEventToDataLayer,
} from '../common/searchUtils';

import {
  getOriginOrDestination,
  getReversedDate,
  getTripType,
  getUTCTimeStamp,
} from '../common/utils';

import {
  getDataFromLocalStorage,
  removeDuplicatedAirports,
  storeDataInLocalStorage,
} from '../common/localstorageUtils';
import { useGeoLocation } from './useGeoLocation';

// Cross-sell imports
import { generateHotelsCrossSellURL } from '../components/CrossSell/utils';

interface SearchContextProps {
  origins: ISearchFlight | undefined;
  destinations: ISearchFlight | undefined;
  flightType: IFlightType;
  flightClass: IFlightClass;
  flightDates: ISearchDate | undefined;
  flightPassagers: IFlightPassagers;
  isMultiCityFlight: boolean;
  searchErrors: ISearchErrors | undefined;
  availableFlightClasses: IFlightClass[];
  isLoading: boolean;
  infoModal: IInfoModal;
  originsHistory: ICustomAutoCompleteSearch[] | undefined;
  destinationsHistory: ICustomAutoCompleteSearch[] | undefined;
  hasSomeSearchError: boolean;
  geolocationSuggestion: ICustomAutoCompleteSearch[] | undefined;
  setOrigins: React.Dispatch<React.SetStateAction<ISearchFlight | undefined>>;
  setDestinations: React.Dispatch<
    React.SetStateAction<ISearchFlight | undefined>
  >;
  setFlightType(flightType: IFlightType): void;
  setFlightClass(flightClass: IFlightClass): void;
  setFlightDates: React.Dispatch<React.SetStateAction<ISearchDate | undefined>>;
  setFlightPassagers(passagers: IFlightPassagers): void;
  setIsMultiCityFlight(value: boolean): void;
  setSearchErrors: React.Dispatch<
    React.SetStateAction<ISearchErrors | undefined>
  >;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setInfoModal: React.Dispatch<React.SetStateAction<IInfoModal>>;
  submitSearch(props?: ISubmitSearch): void;
}

interface DataLayerData {
  searchTerm: string;
  passengers: string;
  searchPeriod: string;
}
const SearchContext = createContext<SearchContextProps>(
  {} as SearchContextProps
);

const pushEventToDataLayer = (dataLayerData: DataLayerData) => {
  window.dataLayer.push({
    event: 'search',
    search_term: dataLayerData.searchTerm,
    passagers: dataLayerData.passengers,
    search_period: dataLayerData.searchPeriod,
  });
};

const SearchProvider: React.FC = ({ children }) => {
  const lastSearch = getDataFromLocalStorage(lastSearchLocalStorageKey);
  const parsedLastSearch = JSON.parse(lastSearch || '[]') as IGenerateSearchURL;

  const [origins, setOrigins] = useState<ISearchFlight>();
  const [destinations, setDestinations] = useState<ISearchFlight>();
  const [flightType, setFlightType] = useState<IFlightType>(
    flightTypesList.ROUND_TRIP
  );
  const [flightClass, setFlightClass] = useState<IFlightClass>(
    flightClassesList.ALL
  );
  const [flightDates, setFlightDates] = useState<ISearchDate>();
  const [flightPassagers, setFlightPassagers] = useState<IFlightPassagers>({
    adults: parsedLastSearch?.flightPassagers?.adults || 1,
    children: parsedLastSearch?.flightPassagers?.children || 0,
    babies: parsedLastSearch?.flightPassagers?.babies || 0,
  });
  const [isMultiCityFlight, setIsMultiCityFlight] = useState(false);
  const [searchErrors, setSearchErrors] = useState<ISearchErrors>();
  const [availableFlightClasses, setAvailableFlightClasses] = useState<
    IFlightClass[]
  >([
    flightClassesList.ALL,
    flightClassesList.ECONOMIC,
    flightClassesList.BUSINESS,
  ]);
  const isRoundTrip = flightType === flightTypesList.ROUND_TRIP;
  const [isLoading, setIsLoading] = useState(false);
  const [infoModal, setInfoModal] = useState<IInfoModal>({
    isOpen: false,
    translationKey: 'generalErrorModal',
  });
  const [originsHistory, setOriginsHistory] =
    useState<ICustomAutoCompleteSearch[]>();
  const [destinationsHistory, setDestinationsHistory] =
    useState<ICustomAutoCompleteSearch[]>();
  const [hasSomeSearchError, setHasSomeSearchError] = useState(false);
  const [geolocationSuggestion, setGeolocationSuggestion] =
    useState<ICustomAutoCompleteSearch[]>();
  const { latitude: lat, longitude: long } = useGeoLocation();
  const checkStoredDate = (date: Date) => {
    const today = new Date();
    if (date.getTime() < today.getTime()) {
      return today;
    }
    return date;
  };

  const isSafari = () => {
    const ua = navigator.userAgent;
    return /Safari/.test(ua) && !/Chrome|CriOS/.test(ua);
  };

  useEffect(() => {
    const hasErrors = Object.values({
      ...searchErrors?.firstLeg,
      ...searchErrors?.secondLeg,
    }).some((isError) => isError === true);
    setHasSomeSearchError(hasErrors);
  }, [searchErrors?.firstLeg, searchErrors?.secondLeg]);

  useEffect(() => {
    if (flightType === 'MULTI_CITY') {
      if (
        (flightDates?.firstFlight?.departureDate &&
          flightDates?.secondFlightDate &&
          flightDates?.firstFlight?.departureDate.getTime() >
            flightDates?.secondFlightDate.getTime()) ||
        (flightDates?.firstFlight?.departureDate &&
          !flightDates?.secondFlightDate)
      ) {
        setFlightDates({
          firstFlight: flightDates?.firstFlight,
          secondFlightDate: flightDates?.firstFlight.departureDate,
        });
      }
    }
  }, [flightDates, flightType]);

  useEffect(() => {
    const lastOrigins = getDataFromLocalStorage(lastOriginsLocalStorageKey);
    const lastDestinations = getDataFromLocalStorage(
      lastDestinationsLocalStorageKey
    );

    if (lastOrigins) {
      setOriginsHistory(JSON.parse(lastOrigins));
    }
    if (lastDestinations) {
      setDestinationsHistory(JSON.parse(lastDestinations));
    }
    if (lastSearch) {
      if (!parsedLastSearch.origins) {
        return;
      }
      const { firstFlight: firstFlightDate, secondFlightDate } =
        parsedLastSearch.flightDates;
      setOrigins(parsedLastSearch.origins);
      setDestinations(parsedLastSearch.destinations);
      setFlightClass(parsedLastSearch.flightClass);
      setFlightType(parsedLastSearch.flightType);
      setIsMultiCityFlight(parsedLastSearch.isMultiCityFlight);
      setFlightDates({
        firstFlight: firstFlightDate && {
          departureDate: checkStoredDate(
            new Date(firstFlightDate.departureDate)
          ),
          returnDate: firstFlightDate.returnDate
            ? checkStoredDate(new Date(firstFlightDate.returnDate))
            : undefined,
        },
        secondFlightDate: secondFlightDate && new Date(secondFlightDate),
      });
      setFlightPassagers(parsedLastSearch.flightPassagers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
  }, []);

  const addNewOriginHistory = (items: ICustomAutoCompleteSearch[]): void => {
    setOriginsHistory((prev) => {
      const noDuplicatedItems = removeDuplicatedAirports({
        currentItems: prev,
        newItems: items,
      }).slice(0, 3);
      storeDataInLocalStorage(
        lastOriginsLocalStorageKey,
        JSON.stringify(noDuplicatedItems)
      );
      return noDuplicatedItems;
    });
  };

  const addNewDestinationHistory = (
    items: ICustomAutoCompleteSearch[]
  ): void => {
    setDestinationsHistory((prev) => {
      const noDuplicatedItems = removeDuplicatedAirports({
        currentItems: prev,
        newItems: items,
      }).slice(0, 3);

      storeDataInLocalStorage(
        lastDestinationsLocalStorageKey,
        JSON.stringify(
          noDuplicatedItems.filter((it) => it.name !== 'Explorar o mundo')
        )
      );
      return noDuplicatedItems;
    });
  };

  useEffect(() => {
    const loadGeoLocation = async (): Promise<void> => {
      const result = await searchAirport({
        location: {
          latitude: lat,
          longitude: long,
        },
      });
      if (!result) {
        return;
      }
      const formatted = result.map((item, index) => ({
        ...item,
        id: `opt_flight_${index + 1}`,
      }));
      setGeolocationSuggestion(formatted);
    };
    loadGeoLocation();
  }, [lat, long]);

  useEffect(() => {
    /* istanbul ignore next */
    function verifyAvailableClasses() {
      if (
        origins &&
        (origins.firstLeg || origins.secondLeg) &&
        destinations &&
        (destinations.firstLeg || destinations.secondLeg)
      ) {
        const airports = [
          origins.firstLeg ? origins.firstLeg : {},
          origins.secondLeg ? origins.secondLeg : {},
          destinations.firstLeg ? destinations.firstLeg : {},
          destinations.secondLeg ? destinations.secondLeg : {},
        ];
        const result = Helpers.getSearchFlightCabin(airports);

        const formattedList = result.reduce(
          (finalResult, item) => {
            if (item.value === flightClassesList.ALL) {
              finalResult.unshift(flightClassesList.ALL);
            }

            if (item.value === flightClassesList.BUSINESS) {
              finalResult.push(flightClassesList.BUSINESS);
            }
            return finalResult;
          },
          [flightClassesList.ECONOMIC]
        );
        /* istanbul ignore next */
        setAvailableFlightClasses(
          formattedList.length > 1 ? ['ALL'] : formattedList
        );
        /* istanbul ignore next */
        setTimeout(() => {
          setAvailableFlightClasses(formattedList);
        }, 500);
      }
    }
    verifyAvailableClasses();
  }, [flightType, origins, destinations]);

  /* istanbul ignore next */
  const hasSearchError = (errors: ISearchErrors | undefined) => {
    return (
      errors?.firstLeg?.origin ||
      errors?.secondLeg?.origin ||
      errors?.firstLeg?.destination ||
      errors?.secondLeg?.destination ||
      errors?.firstLeg?.date ||
      errors?.secondLeg?.date
    );
  };

  /* istanbul ignore next */
  const roundTrip = (flightDate: ISearchDate | undefined) => {
    return (
      isRoundTrip &&
      (!flightDate?.firstFlight?.departureDate ||
        !flightDate?.firstFlight?.returnDate)
    );
  };

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
  }, []);

  /* istanbul ignore next */
  const submitSearch = async (props?: ISubmitSearch) => {
    setIsLoading(true);
    const ignoreSameDayFlightModal = props?.ignoreSameDayFlightModal || false;
    if (hasSearchError(searchErrors)) {
      setIsLoading(false);
      return;
    }
    if (
      !origins?.firstLeg ||
      !destinations?.firstLeg ||
      !flightDates ||
      roundTrip(flightDates) ||
      (isMultiCityFlight &&
        (!origins?.secondLeg ||
          !destinations?.secondLeg ||
          !flightDates?.firstFlight?.departureDate ||
          !flightDates?.secondFlightDate))
    ) {
      setSearchErrors({
        firstLeg: {
          origin: !origins?.firstLeg,
          destination: !destinations?.firstLeg,
          date: !flightDates?.firstFlight?.departureDate,
        },
        secondLeg: isMultiCityFlight
          ? {
              origin: !origins?.secondLeg,
              destination: !destinations?.secondLeg,
              date: !flightDates?.secondFlightDate,
            }
          : {},
      });
      setIsLoading(false);
      return;
    }

    const isMultiCityFlightValid = isMultiCityFlight
      ? Helpers.isMultipleLegsValid('3', [
          origins.firstLeg,
          destinations.firstLeg,
          origins.secondLeg,
          destinations.secondLeg,
        ])
      : true;

    if (!isMultiCityFlightValid) {
      setInfoModal({
        isOpen: true,
        translationKey: getModalType.INVALID_MULTICITY,
      });
      setIsLoading(false);
      return;
    }

    const urlProps = {
      origins,
      destinations,
      flightType,
      flightClass,
      isMultiCityFlight,
      flightDates,
      flightPassagers,
    };

    const urlPropsLegacy = {
      tooltipcount: 1,
      tripType: getTripType(flightType),
      adults: flightPassagers.adults,
      children: flightPassagers.children,
      totalPassenger: flightPassagers.adults + flightPassagers.children,
      departureDay1: getUTCTimeStamp(flightDates.firstFlight?.departureDate),
      departureDay2: getUTCTimeStamp(flightDates.firstFlight?.returnDate),
      returnDay: getUTCTimeStamp(flightDates.firstFlight?.returnDate),
      originAirport: origins.firstLeg.code || '',
      destinAirport: destinations.firstLeg.code || '',
      tripOAirport: origins.secondLeg?.code || '',
      tripDAirport: destinations.secondLeg?.code || '',
      changeDestination: null,
      'flexible-date': false,
      isFlexibleDateChecked: false,
      flexibleDateOptions: null,
      dataAirportOrigin: origins.firstLeg?.description
        ? [origins.firstLeg.description]
        : [],
      dataAirportDeparture: destinations.firstLeg?.description
        ? [destinations.firstLeg.description]
        : [],
      inputOrigin: getOriginOrDestination(origins.firstLeg),
      inputDestination: getOriginOrDestination(destinations.firstLeg),
      inputOriginMs2: getOriginOrDestination(origins.secondLeg),
      inputDestinationMs2: getOriginOrDestination(destinations.secondLeg),
      'single-leg': false,
      departure_date: getReversedDate(flightDates.firstFlight?.departureDate),
      return_date: getReversedDate(flightDates.firstFlight?.returnDate),
      qtdAdult: flightPassagers.adults,
      qtdChild: flightPassagers.children,
      qtdBaby: flightPassagers.babies,
      cabin: cabinTypesList[flightClass || 1],
    };

    const result = generateSearchUrl(urlProps);

    if (!result) {
      setInfoModal({
        isOpen: true,
        translationKey: getModalType.GENERAL_ERROR,
      });
      setIsLoading(false);
      return;
    }

    const getISODate = (date: Date): string => {
      return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    };

    if (
      !ignoreSameDayFlightModal &&
      flightDates.firstFlight &&
      getISODate(flightDates.firstFlight?.departureDate) ===
        getISODate(new Date())
    ) {
      setInfoModal({
        isOpen: true,
        translationKey:
          urlProps.origins.firstLeg?.country ===
          urlProps.destinations.firstLeg?.country
            ? getModalType.SAME_DAY
            : getModalType.SAME_DAY_INTERNATIONAL,
      });
      setIsLoading(false);
      return;
    }

    addNewOriginHistory(
      isMultiCityFlight && origins.secondLeg
        ? [{ ...origins.firstLeg }, { ...origins.secondLeg }]
        : [{ ...origins.firstLeg }]
    );

    addNewDestinationHistory(
      isMultiCityFlight && destinations.secondLeg
        ? [{ ...destinations.firstLeg }, { ...destinations.secondLeg }]
        : [{ ...destinations.firstLeg }]
    );

    storeDataInLocalStorage(
      lastSearchLocalStorageKey,
      JSON.stringify(urlProps)
    );

    storeDataInLocalStorage(
      lastSearchLocalStorageKeyLegacy,
      JSON.stringify(urlPropsLegacy)
    );

    const productionUrl = result
      .replace('portal-hml5.', '')
      .replace('undefined', 'emissao-com-milhas');
    try {
      pushEventToDataLayer(
        formatEventToDataLayer({
          departureCountry: urlProps.origins.firstLeg?.country,
          returnCountry: urlProps.destinations.firstLeg?.country,
          flightType: urlProps.flightType,
          flightPassengers: urlProps.flightPassagers,
          departureDate: urlProps.flightDates.firstFlight?.departureDate,
          returnDate: urlProps.flightDates.firstFlight?.returnDate,
          title: urlProps.destinations.firstLeg?.title,
        })
      );
    } catch (excpetion) {
      console.warn('Erro ao enviar parametros para datalayer', excpetion);
    }

    if (destinations.firstLeg.title === 'Explorar o mundo') {
      const urlExploreWorld = {
        originAirportCode: origins.firstLeg.code,
        departureDate: getReversedDate(flightDates.firstFlight?.departureDate),
        returnDate: '',
        adults: flightPassagers.adults,
        children: flightPassagers.children,
        infants: flightPassagers.babies,
        cabin: cabinTypesList[flightClass || 1],
        tripType: getTripType(flightType),
        originCity: origins.firstLeg.city,
        originCountry: origins.firstLeg.country,
      };
      if (flightType !== 'ONE_WAY') {
        urlExploreWorld.returnDate = getReversedDate(
          flightDates.firstFlight?.returnDate
        );
      }
      localStorage.setItem('exploreWorld', JSON.stringify(urlExploreWorld));
      window.location.assign(`${window.location.origin}/mfe/explorar-mundo`);
    } else if (!props?.isCrossSellChecked) {
      window.location.assign(productionUrl);
    } else {
      const hotelsParams = await generateHotelsCrossSellURL(result, urlProps);
      const crossSellUrl = `${window.location.origin}/mfe/hoteis/resultados?${hotelsParams}`;

      if (!isSafari()) {
        window.open(productionUrl, '_blank');
        window.location.assign(crossSellUrl);
      } else {
        window.location.assign(crossSellUrl);
        window.open(productionUrl, '_blank');
      }
    }
  };

  return (
    <SearchContext.Provider
      value={{
        origins,
        setOrigins,
        destinations,
        setDestinations,
        flightType,
        setFlightType,
        flightClass,
        setFlightClass,
        flightDates,
        setFlightDates,
        flightPassagers,
        setFlightPassagers,
        isMultiCityFlight,
        setIsMultiCityFlight,
        searchErrors,
        setSearchErrors,
        availableFlightClasses,
        isLoading,
        setIsLoading,
        infoModal,
        setInfoModal,
        submitSearch,
        originsHistory,
        destinationsHistory,
        hasSomeSearchError,
        geolocationSuggestion,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

function useSearch(): SearchContextProps {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be declared inside a SearchProvider');
  }
  return context;
}

export { SearchContext, SearchProvider, pushEventToDataLayer, useSearch };
